import { useState } from "react";
import api from "../lib/api";
import { useAgentAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Search,
  Send,
  User2,
  CheckCircle2,
  Wallet,
  Sparkles,
  AlertCircle,
  Hash,
  Mail,
  AtSign,
  Phone,
  ChevronRight,
  Receipt,
  X,
  PartyPopper,
  Clipboard,
} from "lucide-react";

const fmt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const QUICKS = [100, 500, 1000, 5000];

export default function DepositToUser() {
  const { agent, refresh } = useAgentAuth();
  const [query, setQuery] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setFoundUser(null);
    try {
      const { data } = await api.get(`/agent/users/search?q=${encodeURIComponent(query.trim())}`);
      setFoundUser(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "User not found");
    } finally {
      setSearching(false);
    }
  };

  const handlePaste = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.readText) {
        toast.error("Paste not supported. Long-press the input to paste.");
        return;
      }
      const text = (await navigator.clipboard.readText()).trim();
      if (!text) {
        toast.error("Clipboard is empty");
        return;
      }
      setQuery(text);
      toast.success("Pasted from clipboard");
    } catch (err) {
      toast.error("Allow clipboard access or paste manually");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!foundUser) return toast.error("Search & confirm a user first");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > Number(agent.balance)) return toast.error("Insufficient agent balance");
    setSubmitting(true);
    try {
      const { data } = await api.post("/agent/deposit", {
        user_query: foundUser.username,
        amount: amt,
        note,
      });
      setLastReceipt(data);
      setSuccessModal({ ...data, sentAmount: amt, recipient: foundUser.username });
      toast.success(`Deposited ${fmt(amt)} to ${foundUser.username}`);
      setAmount("");
      setNote("");
      refresh();
      try {
        const { data: u } = await api.get(`/agent/users/search?q=${encodeURIComponent(foundUser.username)}`);
        setFoundUser(u);
      } catch {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deposit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const usdNum = parseFloat(amount) || 0;
  const insufficient = usdNum > Number(agent?.balance || 0);

  // Step indicator
  const step = !foundUser ? 1 : !amount ? 2 : 3;

  return (
    <div data-testid="agent-deposit-page" className="max-w-[1300px] mx-auto">
      {/* Header */}
      <div className="mb-5 sm:mb-6">
        <span className="inline-flex items-center gap-1.5 text-[9.5px] sm:text-[10px] font-bold uppercase tracking-[1.8px] sm:tracking-[2px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-2.5 py-0.5 sm:py-1">
          <Sparkles size={10} /> Instant Transfer
        </span>
        <h1 className="text-[22px] sm:text-[28px] font-black tracking-tight mt-2.5 sm:mt-3 leading-[1.1]">
          Deposit to <span className="bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent">a player</span>
        </h1>
        <p className="text-[12.5px] sm:text-sm text-white/55 mt-2 max-w-2xl leading-relaxed">
          <span className="sm:hidden">Search by username, player ID or email — funds transfer instantly.</span>
          <span className="hidden sm:inline">Search a player by username, player ID, or email. Funds transfer from your balance instantly — your wallet updates the moment the action completes.</span>
        </p>
      </div>

      {/* Step indicator — compact on mobile */}
      <div className="flex items-center gap-1.5 sm:gap-2 mb-5 sm:mb-6 overflow-hidden">
        <StepBubble n={1} active={step >= 1} done={step > 1} label="Find player" isCurrent={step === 1} />
        <StepConnector active={step > 1} />
        <StepBubble n={2} active={step >= 2} done={step > 2} label="Set amount" isCurrent={step === 2} />
        <StepConnector active={step > 2} />
        <StepBubble n={3} active={step >= 3} done={false} label="Confirm" isCurrent={step === 3} />
      </div>

      {/* Receipt */}
      {lastReceipt && (
        <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 mb-6"
          style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.16) 0%, rgba(8,12,22,0.85) 50%, rgba(245,158,11,0.10) 100%)" }}
          data-testid="agent-deposit-receipt"
        >
          <div aria-hidden className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-emerald-500/15 blur-[80px]" />
          <div className="relative flex items-start gap-4 p-5">
            <div className="h-12 w-12 rounded-xl grid place-items-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#34d399,#059669)", boxShadow: "0 12px 30px -10px rgba(16,185,129,0.6)" }}>
              <CheckCircle2 className="text-[#06140b]" size={22} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Receipt size={13} className="text-emerald-300" />
                <span className="text-[10.5px] uppercase tracking-[1.8px] text-emerald-300/85 font-bold">Receipt #{(lastReceipt.id || "").slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="text-[16px] font-extrabold mt-1 tracking-tight">
                <span className="font-mono text-emerald-300">{fmt(lastReceipt.amount)}</span>
                <span className="text-white/65 mx-1.5">credited to</span>
                <span className="font-mono text-amber-300">@{lastReceipt.user.username}</span>
              </div>
              <div className="mt-2 flex items-center gap-3 text-[11.5px] text-white/55 flex-wrap">
                <span>Your new balance:</span>
                <span className="font-mono font-bold text-emerald-300">{fmt(lastReceipt.agent_balance)}</span>
                <span className="text-white/25">·</span>
                <span>{new Date().toLocaleString()}</span>
              </div>
            </div>
            <button onClick={() => setLastReceipt(null)} className="text-white/40 hover:text-white text-[11px] flex-shrink-0">Dismiss</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(380px,460px)] gap-4 sm:gap-5">
        {/* LEFT — Search */}
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.08]"
          style={{ background: "linear-gradient(180deg, rgba(13,22,38,0.92) 0%, rgba(10,17,30,0.92) 100%)" }}
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-5">
              <div className="h-9 w-9 rounded-xl grid place-items-center flex-shrink-0"
                style={{ background: "rgba(52,211,153,0.15)", border: "1px solid rgba(52,211,153,0.3)" }}>
                <Search size={15} className="text-emerald-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] sm:text-[14px] font-extrabold tracking-tight">Find player</div>
                <div className="text-[11px] text-white/45 truncate">Username, player ID, or email</div>
              </div>
            </div>

            <form onSubmit={search} className="flex flex-col sm:flex-row gap-2 sm:gap-2.5" data-testid="agent-search-form">
              <div className="relative flex-1 group">
                <AtSign size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-emerald-400 transition" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="username, player ID, or email"
                  className="w-full h-12 pl-10 pr-[88px] bg-[#070b14] border border-white/10 rounded-xl text-[15px] sm:text-sm focus:border-emerald-500/60 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition"
                  data-testid="agent-search-input"
                />
                {/* Right action: Clear (when text) OR Paste (when empty) */}
                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="h-7 w-7 grid place-items-center rounded-md text-white/40 hover:text-white hover:bg-white/[0.06] transition"
                      aria-label="Clear"
                      data-testid="agent-search-clear"
                    >
                      <X size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handlePaste}
                    className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10.5px] font-bold uppercase tracking-[1.2px] text-emerald-300/90 hover:text-emerald-200 bg-emerald-500/[0.08] hover:bg-emerald-500/[0.16] border border-emerald-500/25 hover:border-emerald-400/50 transition active:scale-95"
                    title="Paste from clipboard"
                    aria-label="Paste from clipboard"
                    data-testid="agent-search-paste"
                  >
                    <Clipboard size={11} strokeWidth={2.5} />
                    <span>Paste</span>
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={searching || !query.trim()}
                className="h-12 px-5 rounded-xl text-[#06140b] font-extrabold inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
                style={{ background: "linear-gradient(135deg,#34d399,#10b981)", boxShadow: "0 10px 24px -10px rgba(16,185,129,0.5)" }}
                data-testid="agent-search-submit"
              >
                <Search size={15} strokeWidth={2.5} /> {searching ? "Searching..." : "Search"}
              </button>
            </form>

            {/* Found user */}
            {foundUser && (
              <div className="mt-5 relative overflow-hidden rounded-xl border border-emerald-500/25 p-4"
                style={{ background: "linear-gradient(135deg, rgba(52,211,153,0.08), rgba(255,255,255,0.02))" }}
                data-testid="agent-found-user"
              >
                <div className="flex items-center gap-3.5">
                  <div className="h-12 w-12 rounded-xl grid place-items-center font-black text-[15px] flex-shrink-0 relative"
                    style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#1a1208", boxShadow: "0 8px 20px -8px rgba(245,158,11,0.55)" }}>
                    {(foundUser.full_name || foundUser.username || "U").charAt(0).toUpperCase()}
                    <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full grid place-items-center border-2 border-[#0d1626]"
                      style={{ background: foundUser.status === "blocked" ? "#ef4444" : "#10b981" }}>
                      <CheckCircle2 size={9} className="text-white" strokeWidth={3} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-[15px] tracking-tight truncate" data-testid="agent-found-user-name">
                      {foundUser.full_name || foundUser.username}
                    </div>
                    <div className="flex items-center gap-2 text-[11.5px] text-white/55 mt-0.5 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <User2 size={10} /> @<span className="font-mono">{foundUser.username}</span>
                      </span>
                      <span className="text-white/20">·</span>
                      <span className="inline-flex items-center gap-1">
                        <Hash size={10} /> <span className="font-mono">{foundUser.player_id}</span>
                      </span>
                      {foundUser.email && (
                        <>
                          <span className="text-white/20">·</span>
                          <span className="inline-flex items-center gap-1 truncate"><Mail size={10} /> {foundUser.email}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 text-[12px]">
                  <MiniStat label="Balance" value={fmt(foundUser.balance)} mono tone="emerald" testid="agent-found-user-balance" />
                  <MiniStat
                    label="Status"
                    value={foundUser.status}
                    tone={foundUser.status === "blocked" ? "rose" : "emerald"}
                  />
                  <MiniStat label="Player ID" value={foundUser.player_id} mono tone="amber" />
                </div>
              </div>
            )}

            {!foundUser && !searching && (
              <div className="mt-4 sm:mt-5 text-center py-7 sm:py-10 px-4 rounded-xl border border-dashed border-white/10 bg-white/[0.01]">
                <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-white/[0.03] mx-auto grid place-items-center mb-2.5 sm:mb-3">
                  <User2 size={18} className="text-white/30" />
                </div>
                <div className="text-[12.5px] font-bold text-white/65">No player loaded</div>
                <div className="text-[11px] text-white/40 mt-0.5">Search above to load a profile and start a deposit.</div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Deposit form */}
        <div className="lg:sticky lg:top-4 space-y-4">
          <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30"
            style={{ background: "linear-gradient(180deg, rgba(13,22,38,0.95) 0%, rgba(10,17,30,0.95) 100%)" }}
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
            <div aria-hidden className="absolute -top-24 -right-24 h-60 w-60 bg-emerald-400/10 rounded-full blur-[80px]" />
            <div className="relative p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0"
                  style={{ background: "linear-gradient(135deg,#34d399,#10b981)", boxShadow: "0 8px 20px -8px rgba(16,185,129,0.55)" }}>
                  <Send size={16} className="text-[#06140b]" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] sm:text-[14px] font-extrabold tracking-tight">Send funds</div>
                  <div className="text-[11px] text-white/45">Available: <span className="font-mono text-emerald-300 font-bold">{fmt(agent?.balance)}</span></div>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="agent-deposit-form">
                {/* Amount */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10.5px] uppercase tracking-[1.5px] text-white/55 font-bold">Amount (BDT)</label>
                    {insufficient && <span className="text-[10.5px] text-rose-300 font-bold inline-flex items-center gap-1"><AlertCircle size={10} /> Exceeds balance</span>}
                  </div>
                  <div className="relative group">
                    <span
                      aria-hidden
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full grid place-items-center text-emerald-300 font-black text-[15px] leading-none border border-emerald-500/30 bg-emerald-500/10"
                      style={{ fontFamily: "'Noto Sans Bengali', system-ui, sans-serif" }}
                    >
                      ৳
                    </span>
                    <input
                      type="number" step="0.01" min="0" value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={!foundUser}
                      placeholder="0.00"
                      className={`w-full h-14 sm:h-16 pl-14 pr-3 bg-[#050a14] border-2 rounded-xl text-[22px] sm:text-2xl font-black tabular-nums tracking-tight focus:outline-none focus:ring-4 transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        insufficient
                          ? "border-rose-500/60 focus:border-rose-500 focus:ring-rose-500/15"
                          : "border-white/10 focus:border-emerald-500/60 focus:ring-emerald-500/15"
                      }`}
                      data-testid="agent-deposit-amount"
                    />
                  </div>
                </div>

                {/* Quick select */}
                <div>
                  <label className="text-[10.5px] uppercase tracking-[1.5px] text-white/55 font-bold mb-2 block">Quick select</label>
                  <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                    {QUICKS.map((q) => {
                      const selected = String(q) === amount;
                      return (
                        <button
                          key={q}
                          type="button"
                          disabled={!foundUser}
                          onClick={() => setAmount(String(q))}
                          className={`relative py-2 sm:py-2.5 rounded-lg border text-center transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                            selected
                              ? "border-emerald-400 bg-emerald-400/[0.08]"
                              : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20"
                          }`}
                        >
                          <div className={`text-[13px] sm:text-[15px] font-black tabular-nums leading-none ${selected ? "text-emerald-300" : "text-white"}`}>{q.toLocaleString()}</div>
                          <div className="text-[8.5px] sm:text-[9px] uppercase tracking-[1.3px] sm:tracking-[1.5px] text-white/40 mt-1">BDT</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Note */}
                <div>
                  <label className="text-[10.5px] uppercase tracking-[1.5px] text-white/55 font-bold mb-2 block">Note <span className="text-white/30 normal-case">(optional)</span></label>
                  <input
                    value={note} onChange={(e) => setNote(e.target.value)}
                    disabled={!foundUser}
                    placeholder="e.g. cash received offline"
                    className="w-full h-11 px-3.5 bg-[#050a14] border border-white/10 rounded-xl text-sm focus:border-emerald-500/60 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="agent-deposit-note"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting || !foundUser || !amount || insufficient}
                  className="group relative w-full h-13 py-3.5 rounded-xl text-[#06140b] font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all overflow-hidden active:scale-[0.98]"
                  style={{
                    background: "linear-gradient(135deg,#34d399 0%,#10b981 50%,#059669 100%)",
                    boxShadow: "0 14px 32px -12px rgba(16,185,129,0.65)",
                  }}
                  data-testid="agent-deposit-submit"
                >
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-700" />
                  <Send size={16} className="relative" strokeWidth={2.5} />
                  <span className="relative text-[14px]">
                    {submitting ? "Sending…" : `Deposit${amount ? ` ${fmt(parseFloat(amount) || 0)}` : ""}`}
                  </span>
                  <ChevronRight size={15} className="relative group-hover:translate-x-1 transition" strokeWidth={2.5} />
                </button>
              </form>
            </div>
          </div>

          {/* Tips */}
          <div className="rounded-2xl border border-white/[0.08] p-3.5 sm:p-4 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={13} className="text-amber-300" />
              <div className="text-[11px] uppercase tracking-[1.6px] text-white/65 font-bold">Pro tips</div>
            </div>
            <ul className="space-y-1.5 text-[11.5px] text-white/55">
              <li className="flex items-start gap-2"><span className="h-1 w-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" /> Always confirm the player ID before sending — transfers are non-reversible.</li>
              <li className="flex items-start gap-2"><span className="h-1 w-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" /> Add a short note for your own records — it's logged in History.</li>
              <li className="flex items-start gap-2"><span className="h-1 w-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" /> Top up via USDT Deposit when your balance runs low.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Deposit Success Modal */}
      {successModal && (
        <DepositSuccessModal
          data={successModal}
          onClose={() => setSuccessModal(null)}
        />
      )}
    </div>
  );
}

function MiniStat({ label, value, mono, tone, testid }) {
  const tones = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
  };
  return (
    <div className="rounded-lg px-3 py-2 bg-white/[0.04] border border-white/[0.06]">
      <div className="text-[9.5px] uppercase tracking-[1.5px] text-white/40 font-bold">{label}</div>
      <div className={`mt-1 font-bold text-[13px] ${mono ? "font-mono tabular-nums" : ""} ${tones[tone] || "text-white"}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function StepBubble({ n, active, done, label, isCurrent }) {
  return (
    <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">
      <div className={`relative h-7 w-7 sm:h-8 sm:w-8 rounded-full grid place-items-center text-[11px] sm:text-[12px] font-black transition-all ${
        done
          ? "bg-emerald-500 text-[#06140b]"
          : active
          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 ring-4 ring-emerald-500/10"
          : "bg-white/[0.04] text-white/40 border border-white/10"
      }`}>
        {done ? <CheckCircle2 size={13} strokeWidth={2.8} /> : n}
      </div>
      {/* Mobile: only show label for the CURRENT step; Desktop: show all */}
      <div
        className={`${isCurrent ? "inline" : "hidden"} sm:inline text-[10.5px] sm:text-[11.5px] font-bold uppercase tracking-[1.2px] sm:tracking-[1.4px] whitespace-nowrap ${
          active ? "text-white" : "text-white/40"
        }`}
      >
        {label}
      </div>
    </div>
  );
}
function StepConnector({ active }) {
  return (
    <div className={`flex-1 h-px mx-1 sm:mx-2 transition ${active ? "bg-emerald-500/50" : "bg-white/10"}`} style={{ minWidth: 12, maxWidth: 80 }} />
  );
}


/* ------------------------------------------------------------------ */
/* Deposit Success Modal — animated celebratory popup                  */
/* ------------------------------------------------------------------ */
function DepositSuccessModal({ data, onClose }) {
  const amount = data.sentAmount ?? data.amount;
  const username = data.recipient || data?.user?.username || "";
  const playerId = data?.user?.player_id || data?.user?.id || "";
  const newBalance = data?.agent_balance;
  const receiptId = (data?.id || "").slice(0, 8).toUpperCase();
  const when = new Date().toLocaleString();

  return (
    <div
      data-testid="deposit-success-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ animation: "ml88-fade-in 220ms ease-out forwards" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{
          background: "radial-gradient(circle at 50% 40%, rgba(16,185,129,0.25) 0%, rgba(2,6,12,0.85) 55%, rgba(0,0,0,0.92) 100%)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      />

      {/* Confetti dots */}
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(18)].map((_, i) => {
          const colors = ["#34d399", "#fbbf24", "#a7f3d0", "#fde68a", "#10b981"];
          const left = (i * 53) % 100;
          const delay = (i * 87) % 1400;
          const dur = 1800 + ((i * 137) % 1400);
          const size = 6 + (i % 4) * 2;
          const color = colors[i % colors.length];
          return (
            <span
              key={i}
              style={{
                position: "absolute",
                left: `${left}%`,
                top: "-20px",
                width: size,
                height: size,
                background: color,
                borderRadius: i % 2 ? "50%" : "2px",
                opacity: 0,
                animation: `ml88-confetti ${dur}ms ${delay}ms ease-in forwards`,
                boxShadow: `0 0 ${size * 2}px ${color}`,
              }}
            />
          );
        })}
      </div>

      {/* Card */}
      <div
        data-testid="deposit-success-card"
        className="relative w-full max-w-md rounded-3xl overflow-hidden border border-emerald-400/30"
        style={{
          background: "linear-gradient(160deg, rgba(13,32,24,0.96) 0%, rgba(8,14,22,0.98) 55%, rgba(28,18,8,0.96) 100%)",
          boxShadow: "0 30px 80px -20px rgba(16,185,129,0.45), 0 0 0 1px rgba(255,255,255,0.04) inset",
          animation: "ml88-pop-in 360ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
        }}
      >
        {/* Top glow */}
        <div aria-hidden className="absolute -top-32 -right-24 h-64 w-64 rounded-full" style={{ background: "rgba(16,185,129,0.32)", filter: "blur(70px)" }} />
        <div aria-hidden className="absolute -bottom-32 -left-24 h-64 w-64 rounded-full" style={{ background: "rgba(245,158,11,0.20)", filter: "blur(80px)" }} />
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/70 to-transparent" />

        {/* Close */}
        <button
          onClick={onClose}
          data-testid="deposit-success-close"
          className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full grid place-items-center text-white/50 hover:text-white hover:bg-white/10 transition"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Body */}
        <div className="relative px-7 pt-9 pb-7 text-center">
          {/* Animated check */}
          <div className="mx-auto relative mb-5" style={{ width: 96, height: 96 }}>
            <div
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(16,185,129,0.55) 0%, rgba(16,185,129,0) 70%)",
                animation: "ml88-pulse 1.6s ease-out infinite",
              }}
            />
            <div
              className="relative h-24 w-24 rounded-full grid place-items-center mx-auto"
              style={{
                background: "linear-gradient(135deg, #34d399 0%, #059669 100%)",
                boxShadow: "0 18px 48px -10px rgba(16,185,129,0.7), inset 0 -4px 12px rgba(0,0,0,0.2)",
              }}
            >
              <CheckCircle2 size={52} strokeWidth={2.6} className="text-[#06140b]" style={{ animation: "ml88-check-pop 480ms 120ms cubic-bezier(0.34,1.56,0.64,1) both" }} />
            </div>
          </div>

          {/* Title */}
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2.4px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1 mb-3">
            <PartyPopper size={11} /> Transfer Complete
          </div>
          <h2 className="text-[26px] font-black tracking-tight leading-tight">
            Deposit <span className="bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent">Successful</span>
          </h2>
          <p className="text-[13px] text-white/55 mt-1.5">
            Funds have been credited to the player's wallet instantly.
          </p>

          {/* Amount big */}
          <div
            className="mt-6 rounded-2xl border border-emerald-500/25 px-5 py-4"
            style={{ background: "linear-gradient(180deg, rgba(16,185,129,0.10) 0%, rgba(8,14,22,0.4) 100%)" }}
          >
            <div className="text-[10.5px] uppercase tracking-[1.8px] text-emerald-300/80 font-bold">Amount Sent</div>
            <div
              data-testid="deposit-success-amount"
              className="font-mono font-black text-emerald-300 mt-1"
              style={{ fontSize: 38, lineHeight: 1, textShadow: "0 0 28px rgba(16,185,129,0.4)" }}
            >
              {fmt(amount)}
            </div>
            <div className="text-[12px] text-white/65 mt-2">
              credited to <span className="font-mono font-bold text-amber-300" data-testid="deposit-success-username">@{username}</span>
              {playerId ? <span className="text-white/35"> · #{playerId}</span> : null}
            </div>
          </div>

          {/* Meta grid */}
          <div className="mt-4 grid grid-cols-2 gap-2.5 text-left">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5">
              <div className="text-[9.5px] uppercase tracking-[1.5px] text-white/40 font-bold flex items-center gap-1">
                <Wallet size={10} /> New Balance
              </div>
              <div className="font-mono font-bold text-emerald-300 text-[14px] mt-0.5" data-testid="deposit-success-new-balance">
                {fmt(newBalance)}
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5">
              <div className="text-[9.5px] uppercase tracking-[1.5px] text-white/40 font-bold flex items-center gap-1">
                <Receipt size={10} /> Receipt #
              </div>
              <div className="font-mono font-bold text-white text-[14px] mt-0.5">
                {receiptId || "—"}
              </div>
            </div>
          </div>

          <div className="text-[10.5px] text-white/40 mt-3">{when}</div>

          {/* CTA */}
          <button
            onClick={onClose}
            data-testid="deposit-success-done"
            className="mt-6 w-full h-11 rounded-xl font-bold text-[14px] text-[#06140b] transition active:scale-[0.98]"
            style={{
              background: "linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)",
              boxShadow: "0 10px 30px -8px rgba(16,185,129,0.55)",
            }}
          >
            Done
          </button>
        </div>
      </div>

      <style>{`
        @keyframes ml88-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes ml88-pop-in {
          0%   { opacity: 0; transform: translateY(20px) scale(0.92); }
          60%  { opacity: 1; transform: translateY(-4px) scale(1.02); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes ml88-check-pop {
          0%   { transform: scale(0.3) rotate(-12deg); opacity: 0; }
          60%  { transform: scale(1.18) rotate(4deg);  opacity: 1; }
          100% { transform: scale(1) rotate(0);        opacity: 1; }
        }
        @keyframes ml88-pulse {
          0%   { transform: scale(0.9); opacity: 0.85; }
          70%  { transform: scale(1.6); opacity: 0;    }
          100% { transform: scale(1.6); opacity: 0;    }
        }
        @keyframes ml88-confetti {
          0%   { transform: translateY(0) rotate(0deg);    opacity: 0;   }
          10%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
